import type { JobCategoryRef } from '@seek/job-category';
import { useTranslations } from '@vocab/react';
import {
  Badge,
  Box,
  Column,
  Columns,
  Secondary,
  Stack,
  Text,
} from 'braid-design-system';
import React, {
  type Ref,
  useRef,
  forwardRef,
  type RefObject,
  useContext,
} from 'react';
import { useSelector, shallowEqual } from 'react-redux';

import { SECTION_CLASSIFY } from 'src/app/shared/constants/pageSectionName';
import { useConfig } from 'src/app/shared/hooks/useConfig';
import { usePayTypeTaxonomy } from 'src/app/shared/taxonomies/usePayTypeTaxonomy';
import { useWorkArrangementTaxonomy } from 'src/app/shared/taxonomies/useWorkArrangementTaxonomy';
import { useWorkTypeTaxonomy } from 'src/app/shared/taxonomies/useWorkTypeTaxonomy';
import { JobCategory } from 'src/app/v1/views/components/JobCategory';
import { JobLocation } from 'src/app/v1/views/components/JobLocation';
import { JobTitle } from 'src/app/v1/views/components/JobTitle';
import { Pay } from 'src/app/v1/views/components/Pay';
import { WorkType } from 'src/app/v1/views/components/WorkType';
import { Workplace } from 'src/app/v1/views/components/Workplace';
import { EditFlowDataContext } from 'src/app/v1/views/context/cajaFlowData';
import { EditablePageSection } from 'src/app/v1/views/sections/EditablePageSection';
import { ContentLoader } from 'src/app/v1/views/shared/ContentLoader';
import type { AppState } from 'src/types/appState';
import type {
  JobLocationRef,
  JobTitleRef,
  WorkplaceRef,
} from 'src/types/formFields';
import type { SectionProps, SectionRef } from 'src/types/sections';

import translations from './.vocab';
import { getPayRange } from './utils/getPayRange';

export const Read = () => {
  const {
    categoryText,
    jobTitle,
    jobLocationText,
    subCategoryText,
    workType,
    workArrangement,
    displayPay,
    minSalary,
    maxSalary,
    payType,
    salaryCurrency,
    salaryText,
  } = useSelector(
    (state: AppState) => ({
      categoryText: state.session.categoryText,
      jobTitle: state.formData.jobTitle,
      jobLocationText: state.session.jobLocationText,
      subCategoryText: state.session.subCategoryText,
      workType: state.formData.workType,
      workArrangement: state.formData.workArrangement,
      displayPay: state.formData.displayPay,
      minSalary: state.formData.minSalary,
      maxSalary: state.formData.maxSalary,
      payType: state.formData.payType,
      salaryCurrency: state.formData.salaryCurrency,
      salaryText: state.formData.salaryText,
    }),
    shallowEqual,
  );

  const { t } = useTranslations(translations);

  const { loading } = useContext(EditFlowDataContext);

  const { getWorkTypeText } = useWorkTypeTaxonomy();

  const { getWorkArrangementText } = useWorkArrangementTaxonomy();
  const { getPayTypeText } = usePayTypeTaxonomy();
  const currency = salaryCurrency ?? 'USD';
  const locale = useConfig().getLocale();

  return (
    <Stack space="large">
      <Columns space="small">
        <Column width="1/4">
          <Text weight="strong">
            {t('classify-section-read-job-title-label')}
          </Text>
        </Column>
        <Column>
          <ContentLoader loading={loading} lines={['50%']} offset>
            <Text
              data={{ testid: 'role-information-section-read-job-title-value' }}
            >
              {jobTitle}
            </Text>
          </ContentLoader>
        </Column>
      </Columns>
      <Columns space="xsmall">
        <Column width="1/4">
          <Text weight="strong">
            {t('classify-section-read-location-label')}
          </Text>
        </Column>
        <Column>
          <ContentLoader loading={loading} lines={['50%']} offset>
            <Text>{jobLocationText}</Text>
          </ContentLoader>
        </Column>
      </Columns>
      <Columns space="xsmall">
        <Column width="1/4">
          <Text weight="strong">
            {t('classify-section-read-workplace-option-label')}{' '}
            <Badge tone="positive">{t('new-badge')}</Badge>
          </Text>
        </Column>
        <Column>
          <ContentLoader loading={loading} lines={['15%']} offset>
            <Text>{getWorkArrangementText(workArrangement)}</Text>
          </ContentLoader>
        </Column>
      </Columns>
      <Columns space="xsmall">
        <Column width="1/4">
          <Text weight="strong">
            {t('classify-section-read-category-label')}
          </Text>
        </Column>
        <Column>
          <ContentLoader loading={loading} lines={['75%']} offset>
            <Text>
              {categoryText} <Secondary> • </Secondary> {subCategoryText}
            </Text>
            {/* Using our JobCategory component here, as a low-risk method to return correctly translated categories
                TODO: remove JOB_CATEGORIES constant, and references to legacy Adcentre (sub)category IDs */}
            <Box hidden>
              <JobCategory />
            </Box>
          </ContentLoader>
        </Column>
      </Columns>
      <Columns space="xsmall">
        <Column width="1/4">
          <Text weight="strong">
            {t('classify-section-read-work-type-label')}
          </Text>
        </Column>
        <Column>
          <ContentLoader loading={loading} lines={['15%']} offset>
            <Text>{getWorkTypeText(workType)}</Text>
          </ContentLoader>
        </Column>
      </Columns>
      <Columns space="small">
        <Column width="1/4">
          <Text weight="strong">
            {t('pay-details-section-read-pay-type-label')}
          </Text>
        </Column>
        <Column>
          <ContentLoader loading={loading} lines={['50%']} offset>
            <Text>{getPayTypeText(payType)}</Text>
          </ContentLoader>
        </Column>
      </Columns>
      <Columns space="xsmall">
        <Column width="1/4">
          <Text weight="strong">
            {t('pay-details-section-read-pay-range-label')}
          </Text>
        </Column>
        <Column>
          <ContentLoader loading={loading} lines={['75%']} offset>
            <Text>
              {getPayRange({
                min: minSalary,
                max: maxSalary,
                currency,
                locale,
              })}
            </Text>
          </ContentLoader>
        </Column>
      </Columns>
      <Columns space="xsmall">
        <Column width="1/4">
          <Text weight="strong">
            {t('pay-details-section-read-hidden-salary-label')}
          </Text>
        </Column>
        <Column>
          <ContentLoader loading={loading} lines={['10%']} offset>
            <Text>
              {displayPay
                ? t('pay-details-section-read-hidden-salary-false')
                : t('pay-details-section-read-hidden-salary-true')}
            </Text>
          </ContentLoader>
        </Column>
      </Columns>
      {displayPay && !loading && (
        <Columns space="xsmall">
          <Column width="1/4">
            <Text weight="strong">
              {t('pay-details-section-read-pay-display-text-label')}
            </Text>
          </Column>
          <Column>
            <ContentLoader loading={loading} lines={['10%']} offset>
              <Text>{salaryText}</Text>
            </ContentLoader>
          </Column>
        </Columns>
      )}
    </Stack>
  );
};

interface EditProps {
  refs: {
    jobTitleRef: RefObject<JobTitleRef>;
    jobLocationRef: RefObject<JobLocationRef>;
    jobCategoryRef: RefObject<JobCategoryRef>;
    workplaceRef: RefObject<WorkplaceRef>;
    payRef?: RefObject<any>;
  };
}

const Edit = (props: EditProps) => (
  <Stack space="medium">
    <JobTitle ref={props.refs.jobTitleRef} />
    <JobLocation
      limitSuggestionsToExistingCountry={true}
      ref={props.refs.jobLocationRef}
    />
    <Workplace ref={props.refs.workplaceRef} />
    <JobCategory ref={props.refs.jobCategoryRef} />
    <WorkType />
    <Pay ref={props.refs.payRef} />
  </Stack>
);

export const ClassifySection = forwardRef(
  (_props: SectionProps, ref: Ref<SectionRef>) => {
    const jobTitleRef = useRef<JobTitleRef>(null);
    const jobLocationRef = useRef<JobLocationRef>(null);
    const jobCategoryRef = useRef<any>(null);
    const workplaceRef = useRef<WorkplaceRef>(null);
    const payRef = useRef<any>();

    const { t } = useTranslations(translations);

    const fields = [
      jobTitleRef,
      jobLocationRef,
      jobCategoryRef,
      workplaceRef,
      payRef,
    ];

    return (
      <EditablePageSection
        title={t('classify-section-title')}
        name={SECTION_CLASSIFY}
        ref={ref}
        fields={fields}
        read={<Read />}
        edit={
          <Edit
            refs={{
              jobTitleRef,
              jobLocationRef,
              jobCategoryRef,
              workplaceRef,
              payRef,
            }}
          />
        }
      />
    );
  },
);
